import {
	CheckOutlined,
	CloseOutlined,
	FieldTimeOutlined,
	MailOutlined,
	QuestionCircleTwoTone
} from '@ant-design/icons';
import { Form, Input, Modal, ModalProps, Select, Tag, Tooltip } from 'antd';
import React, { memo } from 'react';
import {
	AiOutlineCloseCircle,
	AiOutlineDollar,
	AiOutlineEdit,
	AiOutlineExport,
	AiOutlineMinusCircle,
	AiOutlinePlusCircle
} from 'react-icons/ai';
import { FiUsers } from 'react-icons/fi';

import { GiReceiveMoney, GiTakeMyMoney } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { MovableCardWrapper } from 'react-trello/dist/styles/Base';
import { ApplicantStates } from '../../../constants/applicant-states';
import { JobApplicationStateMachine } from '../../../containers/dashboard/candidates/constants';
import { AppRoutes } from '../../../enums';
import { Role } from '../../../enums/role';
import { useMedia } from '../../../hooks';
import authService from '../../../services/auth.service';
import { CandidateType } from '../../../types';
import { IMenuAction } from '../../../types/card-options';
import { getCandidateTypeFromCode } from '../../../utils';
import getFormattedCurrentSalary from '../../../utils/formatted-currentSalary';
import { getFormattedSalary } from '../../../utils/formatted-salary';
import { getNoticePeriod } from '../../../utils/notice-period';
import { camelCaseToWord } from '../../../utils/strings';
import { AcpcMark } from '../../acpc-mark/acpc-mark';
import ActionsMenu, { emptyOptions } from '../../base/data-display/actions-menu';
import { ComponentGuard } from '../../component-guard';
import { PSUTMark } from '../../psut-mark/psut-mark';
import { ButtonType, TAButton } from '../../widgets';
import { CandidateAvatar } from './candidate-avatar';
import { ColoredBox, LinkButton } from './styles';

const ColorOption = props => {
	const { children, color, label, size } = props;
	return (
		<span style={{ display: 'flex', alignItems: 'center' }}>
			<ColoredBox color={color} size={size} />
			<span>{label || children}</span>
		</span>
	);
};

const tenantLogoMapper = {
	[CandidateType.ACPC]: <AcpcMark />,
	[CandidateType.PSUT]: <PSUTMark />
};

const LabelForm: React.FC<any> = ({ colorsList, visible, onCancel, onSubmit }) => {
	const [form] = Form.useForm();

	return (
		<Form
			layout="vertical"
			style={{
				width: '100%',
				marginTop: '3px',
				display: visible ? 'block' : 'none'
			}}
			onFinish={async values => {
				onSubmit && (await onSubmit(values));
				form.resetFields();
			}}
			form={form}
		>
			<Form.Item name="title" rules={[{ required: true, message: 'Please enter a label title.' }]}>
				<Input placeholder="Label title" className="ta-ant-input" />
			</Form.Item>
			<Form.Item name="color" rules={[{ required: true, message: 'Please choose a color.' }]}>
				{colorsList && (
					<Select
						className="ta-ant-input mt-1"
						style={{ width: '100%' }}
						placeholder="Select color"
						showSearch
						filterOption={(input, option) =>
							option?.props.children.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
						}
					>
						{colorsList.map(color => (
							<Select.Option value={color.value} key={color.value}>
								<ColorOption color={color.value} size={20}>
									{color.name || color.value}
								</ColorOption>
							</Select.Option>
						))}
					</Select>
				)}
			</Form.Item>
			<Form.Item>
				<TAButton btntype={ButtonType.PRIMARY} className="w-100 mt-1" htmlType="submit">
					Add
				</TAButton>
			</Form.Item>
			<Form.Item>
				<TAButton
					btntype={ButtonType.DANGER}
					className="w-100 mt-1"
					onClick={() => {
						form.resetFields();
						onCancel && onCancel();
					}}
				>
					Cancel
				</TAButton>
			</Form.Item>
		</Form>
	);
};

export const CandidateCard = memo<ModalProps & any>(
	({
		onClick,
		className,
		cardStyle,
		application,
		state,
		isRejectedMailSent,
		labelStyle,
		labels,
		onLabelDelete,
		onSendRejectionMessage,
		onLabelFormSubmit,
		colorsList,
		bgColor,
		title,
		subtitle,
		currentSalary,
		expectedSalary,
		noticePeriod,
		avatar = true,
		draggable = true,
		labelEditing = false,
		removable,
		onRemove,
		moveCard,
		onEditApplication,
		onShowOffer,
		id,
		isAssigned,
		// onAddReport,
		backOffice
	}) => {
		const confirmCardRemove = card => {
			Modal.confirm({
				title: `Are you sure you want to delete this application?`,
				onOk: () => onRemove(card),
				okButtonProps: {
					type: 'primary'
				},
				okText: 'Delete',
				icon: <QuestionCircleTwoTone twoToneColor="#ff7875" />
			});
		};
		const [isLabelFormVisible, setLabelFormVisible] = React.useState(false);
		const [isRejectedMailSentLocal, setIsRejectedMailSentLocal] = React.useState('');
		const [isDotsMenuVisible, setDotsMenuVisible] = React.useState(false);
		const navigate = useNavigate();

		const getCardOptions = () => {
			const options: IMenuAction[] = [];
			if (backOffice)
				options.push({
					name: 'Edit Application',
					key: 'editApplication',
					icon: <AiOutlineEdit />,
					action: () => onEditApplication(application)
				});

			if (application.offer)
				options.push({
					name: 'Show Offer',
					key: 'showOffer',
					icon: <AiOutlineDollar />,
					action: () => onShowOffer(application.offer, application)
				});

			if (JobApplicationStateMachine[state].length && draggable)
				options.push({
					name: 'Move To',
					key: 'moveTo',
					icon: <AiOutlineExport />,
					submenu: JobApplicationStateMachine[state].map(destination => ({
						name: camelCaseToWord(destination),
						key: destination,
						action: () => moveCard(id, state, destination)
					}))
				});

			if (removable)
				options.push({
					name: 'Remove',
					key: 'remove',
					icon: <AiOutlineCloseCircle />,
					action: () => confirmCardRemove(application),
					tooltipText: 'This will remove the candidate from interested list'
				});

			return options;
		};
		const largeScreens = useMedia('lg');
		const card = (
			<MovableCardWrapper onClick={onClick} style={{ padding: '10px 0', ...cardStyle }} className={className}>
				<div
					onMouseEnter={() => setDotsMenuVisible(true)}
					onMouseLeave={() => setDotsMenuVisible(false)}
					style={{ padding: '0 10px' }}
				>
					<div
						style={{
							fontSize: 12,
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',

							whiteSpace: 'break-spaces',
							userSelect: 'none',
							position: 'relative'
						}}
					>
						<ActionsMenu
							dotsVisible={!largeScreens ? true : isDotsMenuVisible}
							options={getCardOptions()}
							dotsOrientation="horizontal"
							dotsStyle={{
								position: 'absolute',
								top: largeScreens ? '0' : '-6px',
								right: largeScreens ? '0' : '-6px',
								marginRight: '0'
							}}
							bordered
							whenEmpty={emptyOptions.DISABLED}
						/>

						<CandidateAvatar
							showAvatar={avatar}
							candidate={application.candidate}
							size={40}
							avatarBgColor={bgColor}
							shape="circle"
						/>

						<div style={{ padding: '5px 0px', flex: '3', marginLeft: '10px', whiteSpace: 'break-spaces' }}>
							<b>{title}</b> {tenantLogoMapper[getCandidateTypeFromCode(title)] || null}
							<br />
							<i>{subtitle || 'NA'}</i>
						</div>
					</div>
					<div className="border-bottom mb-2 pb-2">
						{currentSalary?.value ? (
							<ComponentGuard
								allowed={authService.hasRoles(
									Role.BACKOFFICE_ADMIN,
									Role.BACKOFFICE_USER,
									Role.BACKOFFICE_SUPER_ADMIN,
									Role.COMPANY_ADMIN,
									Role.COMPANY_OWNER
								)}
								fallback={undefined}
							>
								<Tooltip title="Current Salary" className="mt-2">
									<div className="d-flex align-items-center">
										<GiReceiveMoney size={15} className="me-1" />
										{getFormattedCurrentSalary(currentSalary)}
									</div>
								</Tooltip>
							</ComponentGuard>
						) : null}
						<ComponentGuard
							allowed={authService.hasRoles(
								Role.BACKOFFICE_ADMIN,
								Role.BACKOFFICE_USER,
								Role.BACKOFFICE_SUPER_ADMIN,
								Role.COMPANY_ADMIN,
								Role.COMPANY_OWNER
							)}
							fallback={undefined}
						>
							<Tooltip title="Expected Salary">
								<div className="d-flex align-items-center">
									<GiTakeMyMoney size={15} className="me-1" />
									{expectedSalary?.min > 0 ? getFormattedSalary(expectedSalary) : 'Negotiable'}
								</div>
							</Tooltip>
						</ComponentGuard>

						{noticePeriod ? (
							<Tooltip title="Notice Period">
								<div className="d-flex align-items-center">
									<FieldTimeOutlined style={{ fontSize: '15px' }} className="me-1" />
									{getNoticePeriod(noticePeriod)}
								</div>
							</Tooltip>
						) : null}
						{isAssigned && (
							<Tooltip title="Assigned to an interview slot">
								<Tag
									color="#005e9d"
									className="m-1"
									style={{ whiteSpace: 'break-spaces', display: 'block', width: 'fit-content' }}
								>
									Assigned
								</Tag>
							</Tooltip>
						)}
						{application?.rejectionReason && (
							<React.Fragment>
								<br />
								<Tooltip title={application?.rejectionReason}>
									<Tag color="red" className="m-1" style={{ whiteSpace: 'break-spaces' }}>
										{application?.rejectionReason}
									</Tag>
								</Tooltip>
							</React.Fragment>
						)}
					</div>

					{labels?.length !== 0 && (
						<div
							className="my-2 py-2"
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								flexDirection: 'row',
								flexWrap: 'wrap'
							}}
							dir="rtl"
						>
							{labels.map(label => (
								<Tag
									color={label.color}
									key={label._id}
									closable={labelEditing}
									onClose={e => {
										e.stopPropagation();
										onLabelDelete && onLabelDelete(label._id);
									}}
									dir="ltr"
									closeIcon={<CloseOutlined className="ms-1" />}
									style={{
										margin: '1px',
										...labelStyle
									}}
									{...label}
								>
									{label.title}
								</Tag>
							))}
						</div>
					)}

					{[ApplicantStates.toInterview, ApplicantStates.offer, ApplicantStates.hired].includes(state) ? (
						<TAButton
							className="mb-3"
							btntype={ButtonType.PRIMARY}
							onClick={e => {
								e.stopPropagation();
								navigate(AppRoutes.OUTSOURCING);
							}}
							icon={<FiUsers className="me-2" size={20} />}
						>
							Outsource Talents
						</TAButton>
					) : (
						false
					)}

					{labelEditing && (
						<div onClick={e => e.stopPropagation()}>
							<LinkButton
								onClick={e => {
									e.preventDefault();
									setLabelFormVisible(!isLabelFormVisible);
								}}
							>
								{isLabelFormVisible ? (
									<AiOutlineMinusCircle className="me-1" />
								) : (
									<AiOutlinePlusCircle className="me-1" />
								)}
								<span className="me-1">Add tag</span>{' '}
							</LinkButton>

							<LabelForm
								colorsList={colorsList}
								onCancel={() => setLabelFormVisible(false)}
								visible={isLabelFormVisible}
								onSubmit={onLabelFormSubmit}
							/>
						</div>
					)}
					{backOffice
						? application.state === 'rejected' && (
								<div onClick={e => e.stopPropagation()}>
									{(() => {
										if (isRejectedMailSent || isRejectedMailSentLocal === application.id) {
											return (
												<div>
													<CheckOutlined className="me-1" />
													<span className="me-1">Sent successfully</span>
												</div>
											);
										} else {
											return (
												<>
													<LinkButton
														onClick={e => {
															e.preventDefault();
															setIsRejectedMailSentLocal(application.id);
															onSendRejectionMessage &&
																onSendRejectionMessage(application);
														}}
													>
														<MailOutlined className="me-1" />
														<span className="me-1">Send rejection mail</span>
													</LinkButton>
												</>
											);
										}
									})()}
								</div>
						  )
						: null}
				</div>
			</MovableCardWrapper>
		);

		return draggable ? (
			card
		) : (
			<Tooltip title="Can't drag this card" style={{ userSelect: 'none' }}>
				{card}
			</Tooltip>
		);
	}
);
